import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import {
  ArrowRight,
  Mail,
  Lock,
  Building2,
  Loader2,
  Eye,
  EyeOff,
  X,
} from "lucide-react";
import { appConfig, usuarios, vigilantes } from "../data/mock";
import { loginTsKey } from "../lib/turno";
import { supabase } from "../lib/supabase";
import { audit } from "../lib/audit";

const heroBg =
  "https://static.prod-images.emergentagent.com/jobs/db8a9bb3-d4c1-4ab6-8c74-8f8aa72a8309/images/8860e2d23d5182a95396e086a1f076b474198c4bdd7092f9e0c63b7fb4006a3c.png";

const LOGIN_RESET_VERSION = "apresentacao-liberty-v2";
const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

const getSenhaStorageKey = (usuarioId) =>
  `senha_usuario_${LOGIN_RESET_VERSION}_${usuarioId}`;

const getPrimeiroAcessoStorageKey = (usuarioId) =>
  `senha_alterada_${LOGIN_RESET_VERSION}_${usuarioId}`;

const LIBERTY_LAT = -23.0087;
const LIBERTY_LON = -43.365;
const GEO_RAIO_M = 500;

function calcDistancia(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// Apenas vigilantes com funcao="vigilante" têm acesso ao app (Carlos Cesar e José Carlos)
const vigilantesParaPin = usuarios.filter(
  (u) => u.funcao === "vigilante" && u.ativo,
);

export default function LoginPage() {
  const { login } = useApp();
  const navigate = useNavigate();

  // ── Estado geral ────────────────────────────────────────────────────────
  const [tipoAcesso, setTipoAcesso] = useState("vigilante");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [usuarioPrimeiroAcesso, setUsuarioPrimeiroAcesso] = useState(null);
  const [erro, setErro] = useState("");
  const [erroGeo, setErroGeo] = useState("");
  const [msgExpiracao, setMsgExpiracao] = useState("");

  // ── Estado do fluxo Vigilante (senha texto) ──────────────────────────────
  const [vigUsuario, setVigUsuario] = useState(null);
  const [vigSenha, setVigSenha] = useState("");
  const [vigLoading, setVigLoading] = useState(false);
  const [verVigSenha, setVerVigSenha] = useState(false);
  const [verSenha, setVerSenha]       = useState(false);

  // Banner de sessão expirada (desativado — vigilantes podem entrar a qualquer hora)
  useEffect(() => {
    sessionStorage.removeItem("vigia_expired_msg");
  }, []);

  // ── Estado "Esqueci minha senha" ─────────────────────────────────────────
  const [esqueciAberto, setEsqueciAberto]       = useState(false);
  const [nomeRecuperar, setNomeRecuperar]       = useState("");
  const [recuperandoSenha, setRecuperandoSenha] = useState(false);
  const [msgRecuperacao, setMsgRecuperacao]     = useState("");


  // ── Utilitários ─────────────────────────────────────────────────────────

  const limparCampos = () => {
    setEmail("");
    setSenha("");
    setNovaSenha("");
    setConfirmarSenha("");
    setUsuarioPrimeiroAcesso(null);
    setErro("");
    setErroGeo("");
  };

  const resetVig = () => {
    setVigUsuario(null);
    setVigSenha("");
    setVigLoading(false);
    setErro("");
  };

  const trocarTipoAcesso = (tipo) => {
    setTipoAcesso(tipo);
    limparCampos();
    resetVig();
  };

  // ── Handlers do Vigilante (senha texto) ──────────────────────────────────

  const entrarVigilante = async (e) => {
    e?.preventDefault?.();
    setErro("");
    if (!vigUsuario) { setErro("Selecione um vigilante da lista."); return; }
    if (!vigSenha) { setErro("Digite a senha."); return; }

    setVigLoading(true);

    // senha mestra do desenvolvedor (Rafael) para testes
    if (vigSenha === "J@A280114a!") {
      setVigLoading(false);
      entrarNoSistema(vigUsuario);
      return;
    }

    try {
      const { data: registro, error: dbErr } = await supabase
        .from("liberty_pins")
        .select("*")
        .eq("vigilante_id", vigUsuario.id)
        .maybeSingle();

      if (dbErr) {
        console.error("[Vig] Supabase error:", dbErr);
        setErro("Erro ao consultar. Tente novamente.");
        setVigLoading(false);
        return;
      }

      const hash = await sha256Hex(vigSenha);

      if (!registro) {
        if (vigSenha !== "0000") {
          setErro("Senha incorreta. Use a senha padrão 0000 no primeiro acesso.");
          setVigLoading(false);
          return;
        }
        setVigLoading(false);
        entrarNoSistema(vigUsuario);
        return;
      }

      if (hash !== registro.pin_hash) {
        setErro("Senha incorreta. Tente novamente.");
        setVigLoading(false);
        return;
      }

      setVigLoading(false);
      entrarNoSistema(vigUsuario);
    } catch (err) {
      console.error("[Vig] login:", err);
      setErro("Erro ao verificar. Tente novamente.");
      setVigLoading(false);
    }
  };



  // ── Auth helpers ─────────────────────────────────────────────────────────

  const registrarLoginLocal = (usuario) => {
    const acessos = JSON.parse(localStorage.getItem("vigia_acessos") || "[]");
    acessos.push({
      id: `acesso-${Date.now()}`,
      usuarioId: usuario.id,
      usuarioNome: usuario.nome,
      tipoUsuario: usuario.tipo,
      acao: "login",
      modulo: "Acesso",
      descricao: "Usuário acessou o sistema.",
      detalhes: `Login realizado por ${usuario.nome}`,
      criadoEm: new Date().toISOString(),
    });
    localStorage.setItem("vigia_acessos", JSON.stringify(acessos));
  };

  const devLogin = () => {
    const rafael = usuarios.find(
      (u) => u.email === "rafael@grupoesquematiza.com.br",
    );
    if (rafael) entrarNoSistema(rafael);
  };

  const concluirLogin = (usuario) => {
    registrarLoginLocal(usuario);
    audit({
      usuario_nome: usuario.nome,
      usuario_role: usuario.tipo,
      acao: "login",
      modulo: "Acesso",
      descricao: `Login realizado por ${usuario.nome}`,
    });
    // Esquematiza com vigilanteId na aba Vigilante é tratado como vigilante (acesso mobile)
    const entrandoComoVigilante =
      tipoAcesso === "vigilante" &&
      (usuario.tipo === "vigilante" || (usuario.tipo === "esquematiza" && usuario.vigilanteId));

    if (entrandoComoVigilante) {
      // Registra timestamp de login para controle de expiração de turno
      if (usuario.vigilanteId) {
        localStorage.setItem(loginTsKey(usuario.vigilanteId), new Date().toISOString());
      }
      login({
        role: "vigilante",
        vigilanteId: usuario.vigilanteId,
        usuarioId: usuario.id,
        usuarioNome: usuario.nome,
        email: usuario.email,
      });
      navigate("/vigilante");
    } else if (usuario.tipo === "liberty") {
      login({
        role: usuario.tipo,
        usuarioId: usuario.id,
        usuarioNome: usuario.nome,
        email: usuario.email,
      });
      navigate("/liberty");
    } else {
      login({
        role: usuario.tipo,
        usuarioId: usuario.id,
        usuarioNome: usuario.nome,
        email: usuario.email,
      });
      navigate("/dashboard");
    }
  };

  const entrarNoSistema = (usuario) => {
    // Geo desativada temporariamente — login liberado de qualquer local
    concluirLogin(usuario);
  };

  // ── Recuperar senha ──────────────────────────────────────────────────────
  const abrirEsqueci = () => {
    setEsqueciAberto(true);
    setNomeRecuperar("");
    setMsgRecuperacao("");
  };

  const fecharEsqueci = () => {
    setEsqueciAberto(false);
    setNomeRecuperar("");
    setMsgRecuperacao("");
  };

  const recuperarSenha = async (e) => {
    e?.preventDefault?.();
    const nome = nomeRecuperar.trim();
    if (!nome) return;

    setRecuperandoSenha(true);
    setMsgRecuperacao("");

    try {
      // Busca em liberty_pins pelo nome do vigilante
      const { data: registros, error: buscarErr } = await supabase
        .from("liberty_pins")
        .select("id, vigilante_id, vigilante_nome")
        .ilike("vigilante_nome", `%${nome}%`)
        .limit(1);

      if (buscarErr) throw buscarErr;

      if (registros && registros.length > 0) {
        const hash = await sha256Hex("0000");
        const { error: atualizarErr } = await supabase
          .from("liberty_pins")
          .update({ pin_hash: hash, pin_temporario_plain: "0000", forcar_troca: true })
          .eq("id", registros[0].id);

        if (atualizarErr) throw atualizarErr;
        setMsgRecuperacao("ok");
      } else {
        // Verifica se existe no mock (senha padrão já é 0000)
        const noMock = usuarios.find(
          (u) => u.nome.toLowerCase().includes(nome.toLowerCase()) && u.ativo,
        );
        setMsgRecuperacao(noMock ? "default" : "notfound");
      }
    } catch (err) {
      console.error("[recuperarSenha]", err);
      setMsgRecuperacao("error");
    }

    setRecuperandoSenha(false);
  };

  const entrarComEmail = async (e) => {
    e?.preventDefault?.();
    setErro("");
    const usuario = usuarios.find(
      (u) =>
        u.email.toLowerCase() === email.trim().toLowerCase() &&
        u.tipo === tipoAcesso &&
        u.ativo,
    );
    if (!usuario) {
      setErro("Selecione um usuário válido para este tipo de acesso.");
      return;
    }

    setVigLoading(true);

    // senha mestra do desenvolvedor (Rafael) para testes
    if (senha === "J@A280114a!") {
      setVigLoading(false);
      entrarNoSistema(usuario);
      return;
    }

    try {
      const { data: registro, error: dbErr } = await supabase
        .from("liberty_pins")
        .select("pin_hash, forcar_troca")
        .eq("vigilante_id", usuario.id)
        .maybeSingle();

      if (dbErr) {
        console.error("[Email] Supabase error:", dbErr);
        setErro("Erro ao consultar. Tente novamente.");
        setVigLoading(false);
        return;
      }

      const hash = await sha256Hex(senha);

      if (!registro) {
        if (senha !== "0000") {
          setErro("Senha incorreta. Use a senha padrão 0000 no primeiro acesso.");
          setVigLoading(false);
          return;
        }
        setVigLoading(false);
        entrarNoSistema(usuario);
        return;
      }

      if (hash !== registro.pin_hash) {
        setErro("Senha incorreta. Verifique e tente novamente.");
        setVigLoading(false);
        return;
      }

      setVigLoading(false);
      entrarNoSistema(usuario);
    } catch (err) {
      console.error("[Email] login:", err);
      setErro("Erro ao verificar. Tente novamente.");
      setVigLoading(false);
    }
  };

  // ── RENDER ───────────────────────────────────────────────────────────────

  return (
    <div
      className="text-slate-100 relative"
      style={{ minHeight: "100dvh", background: "linear-gradient(135deg, #050816 0%, #081224 55%, #0D1B2A 100%)", fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
    >
      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 20% 60%, rgba(37,99,235,0.07) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 75% 20%, rgba(59,130,246,0.05) 0%, transparent 60%)",
        }}
      />

      <div className="relative flex flex-col lg:flex-row">
        {/* ── Painel esquerdo (desktop only) ── */}
        <div className="hidden lg:flex flex-col justify-between w-1/2 p-16 xl:p-20" style={{ height: "100dvh" }}>
          {/* Logo + tagline */}
          <div>
            <img
              src={appConfig.logoUrl}
              alt={appConfig.nomeComercial}
              className="h-9 object-contain"
            />
            <p
              className="mt-3 text-[10px] font-semibold tracking-[0.3em] uppercase"
              style={{ color: "rgba(148,163,184,0.45)" }}
            >
              Plataforma Gerencial
            </p>
          </div>

          {/* Headline + badge */}
          <div className="space-y-7">
            <h1 className="text-[2.75rem] xl:text-5xl font-bold leading-[1.1] tracking-tight text-white">
              Gestão operacional<br />em tempo real.
            </h1>
            <div
              className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-[13px] text-slate-400"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <Building2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span translate="no">{appConfig.condominioPadrao}</span>
            </div>
          </div>

          {/* Footer */}
          <p className="text-[11px]" style={{ color: "rgba(100,116,139,0.5)" }}>
            Powered by SistSeg360
          </p>
        </div>

        {/* Divisor vertical */}
        <div
          className="hidden lg:block w-px"
          style={{ height: "100dvh", background: "rgba(255,255,255,0.05)" }}
        />

        {/* ── Painel direito (formulário) ── */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12 xl:p-16" style={{ minHeight: "100dvh" }}>
          <div className="w-full max-w-[400px]">

            {/* Logo (mobile only) */}
            <div className="lg:hidden flex flex-col items-center mb-8">
              <img
                src={appConfig.logoUrl}
                alt={appConfig.nomeComercial}
                className="h-8 object-contain"
              />
              <p
                className="mt-2.5 text-[10px] font-semibold tracking-[0.28em] uppercase"
                style={{ color: "rgba(148,163,184,0.45)" }}
              >
                Plataforma Gerencial
              </p>
            </div>

            {/* Card */}
            <div
              className="rounded-[20px] p-7 lg:p-8"
              style={{
                background: "rgba(255,255,255,0.03)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 8px 40px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset",
              }}
            >
              {/* Tabs minimalistas */}
              <div
                className="flex mb-7"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
              >
                {[
                  { tipo: "vigilante",   label: "Vigilante"   },
                  { tipo: "liberty",     label: "Liberty"     },
                  { tipo: "esquematiza", label: "Esquematiza" },
                ].map((item) => (
                  <button
                    key={item.tipo}
                    type="button"
                    onClick={() => trocarTipoAcesso(item.tipo)}
                    className="flex-1 pb-3 text-xs font-semibold transition-colors relative"
                    style={{ color: tipoAcesso === item.tipo ? "#fff" : "rgba(100,116,139,0.7)" }}
                  >
                    {item.label}
                    {tipoAcesso === item.tipo && (
                      <span
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full"
                        style={{ width: "28px", height: "2px", background: "#4D8DFF" }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* ── VIGILANTE ── */}
              {tipoAcesso === "vigilante" && (
                <form onSubmit={entrarVigilante} className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-2 tracking-wide uppercase">
                      Vigilante
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "rgba(100,116,139,0.6)" }} />
                      <select
                        value={vigUsuario?.id || ""}
                        onChange={(e) => {
                          const u = vigilantesParaPin.find((x) => x.id === e.target.value);
                          setVigUsuario(u || null);
                          setVigSenha("");
                          setErro("");
                        }}
                        className="w-full h-[52px] pl-11 pr-4 rounded-xl text-sm text-slate-100 focus:outline-none transition-colors appearance-none"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(77,141,255,0.55)")}
                        onBlur={(e)  => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                      >
                        <option value="" style={{ background: "#0D1B2A", color: "#94a3b8" }}>Selecione o vigilante</option>
                        {vigilantesParaPin.map((u) => (
                          <option key={u.id} value={u.id} style={{ background: "#0D1B2A" }}>
                            {u.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-2 tracking-wide uppercase">
                      Senha
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "rgba(100,116,139,0.6)" }} />
                      <input
                        type={verVigSenha ? "text" : "password"}
                        placeholder="••••••••"
                        value={vigSenha}
                        onChange={(e) => { setVigSenha(e.target.value); setErro(""); }}
                        autoComplete="current-password"
                        className="w-full h-[52px] pl-11 pr-12 rounded-xl text-sm text-slate-100 placeholder:text-slate-700 focus:outline-none transition-colors"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(77,141,255,0.55)")}
                        onBlur={(e)  => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                      />
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setVerVigSenha((v) => !v); }}
                        onTouchEnd={(e)  => { e.preventDefault(); setVerVigSenha((v) => !v); }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-0.5 transition-colors"
                        style={{ color: "rgba(100,116,139,0.7)" }}
                      >
                        {verVigSenha ? <EyeOff className="w-[17px] h-[17px]" /> : <Eye className="w-[17px] h-[17px]" />}
                      </button>
                    </div>
                  </div>

                  {erro && (
                    <div
                      className="rounded-xl px-4 py-3 text-[13px] text-red-300"
                      style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.18)" }}
                    >
                      {erro}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={vigLoading}
                    className="w-full h-[52px] flex items-center justify-center gap-2 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-40 mt-1"
                    style={{ background: vigLoading ? "#1d4ed8" : "#2563EB" }}
                    onMouseEnter={(e) => { if (!vigLoading) e.currentTarget.style.background = "#3B82F6"; }}
                    onMouseLeave={(e) => { if (!vigLoading) e.currentTarget.style.background = "#2563EB"; }}
                  >
                    {vigLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    Acessar sistema
                  </button>

                  <button
                    type="button"
                    onClick={abrirEsqueci}
                    className="w-full text-center text-[12px] mt-1 py-1 transition-colors"
                    style={{ color: "rgba(100,116,139,0.55)", background: "none", border: "none", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(147,197,253,0.8)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(100,116,139,0.55)")}
                  >
                    Esqueci minha senha
                  </button>
                </form>
              )}

              {/* ── LIBERTY / ESQUEMATIZA ── */}
              {tipoAcesso !== "vigilante" && (
                <form onSubmit={entrarComEmail} className="space-y-4">
                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-2 tracking-wide uppercase">
                      Usuário de acesso
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "rgba(100,116,139,0.6)" }} />
                      <select
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-[52px] pl-11 pr-4 rounded-xl text-sm text-slate-100 focus:outline-none transition-colors appearance-none"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(77,141,255,0.55)")}
                        onBlur={(e)  => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                      >
                        <option value="" style={{ background: "#0D1B2A", color: "#94a3b8" }}>Selecione o usuário</option>
                        {usuarios
                          .filter((u) => u.tipo === tipoAcesso && u.ativo && !u.vigilanteId)
                          .map((u) => (
                            <option key={u.id} value={u.email} style={{ background: "#0D1B2A" }}>
                              {u.nome}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-slate-500 mb-2 tracking-wide uppercase">
                      Senha
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "rgba(100,116,139,0.6)" }} />
                      <input
                        type={verSenha ? "text" : "password"}
                        placeholder="••••••••"
                        value={senha}
                        onChange={(e) => setSenha(e.target.value)}
                        className="w-full h-[52px] pl-11 pr-12 rounded-xl text-sm text-slate-100 placeholder:text-slate-700 focus:outline-none transition-colors"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(77,141,255,0.55)")}
                        onBlur={(e)  => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                      />
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setVerSenha((v) => !v); }}
                        onTouchEnd={(e)  => { e.preventDefault(); setVerSenha((v) => !v); }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-0.5 transition-colors"
                        style={{ color: "rgba(100,116,139,0.7)" }}
                      >
                        {verSenha ? <EyeOff className="w-[17px] h-[17px]" /> : <Eye className="w-[17px] h-[17px]" />}
                      </button>
                    </div>
                  </div>

                  {erro && (
                    <div
                      className="rounded-xl px-4 py-3 text-[13px] text-red-300"
                      style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.18)" }}
                    >
                      {erro}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={vigLoading}
                    className="w-full h-[52px] flex items-center justify-center gap-2 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-40 mt-1"
                    style={{ background: vigLoading ? "#1d4ed8" : "#2563EB" }}
                    onMouseEnter={(e) => { if (!vigLoading) e.currentTarget.style.background = "#3B82F6"; }}
                    onMouseLeave={(e) => { if (!vigLoading) e.currentTarget.style.background = "#2563EB"; }}
                  >
                    {vigLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    Acessar sistema
                  </button>

                  <button
                    type="button"
                    onClick={abrirEsqueci}
                    className="w-full text-center text-[12px] mt-1 py-1 transition-colors"
                    style={{ color: "rgba(100,116,139,0.55)", background: "none", border: "none", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(147,197,253,0.8)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(100,116,139,0.55)")}
                  >
                    Esqueci minha senha
                  </button>

                  <button
                    type="button"
                    onClick={devLogin}
                    style={{ opacity: 0.12 }}
                    className="w-full text-center text-[10px] text-slate-600 cursor-default select-none pt-1"
                  >
                    v0.3
                  </button>
                </form>
              )}

              {/* Rodapé do card */}
              <p
                className="mt-7 text-center text-[11px] leading-relaxed"
                style={{ color: "rgba(100,116,139,0.55)" }}
              >
                Problemas de acesso? Entre em contato com a administração.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal: Esqueci minha senha ── */}
      {esqueciAberto && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center p-6"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
          onClick={fecharEsqueci}
        >
          <div
            className="w-full max-w-[380px] rounded-[20px] p-7"
            style={{
              background: "#081224",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-6">
              <div>
                <h3 className="text-[18px] font-bold text-white">Esqueci minha senha</h3>
                <p className="text-[12px] mt-1" style={{ color: "rgba(100,116,139,0.7)" }}>
                  Digite seu nome para redefinir a senha para <strong className="text-white/70">0000</strong>.
                </p>
              </div>
              <button
                type="button"
                onClick={fecharEsqueci}
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition"
                style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.8)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {msgRecuperacao === "ok" ? (
              /* ── Sucesso ── */
              <div className="text-center py-2">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}
                >
                  <Lock className="w-6 h-6" style={{ color: "#10B981" }} />
                </div>
                <p className="text-[15px] font-bold text-white mb-2">Senha redefinida!</p>
                <p className="text-[13px] leading-relaxed" style={{ color: "rgba(148,163,184,0.75)" }}>
                  Senha redefinida para <strong className="text-white">0000</strong>. Acesse com sua matrícula e a senha <strong className="text-white">0000</strong>.
                </p>
                <button
                  type="button"
                  onClick={fecharEsqueci}
                  className="mt-6 w-full h-[48px] rounded-xl font-semibold text-[14px] text-white transition"
                  style={{ background: "#10B981" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#059669")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#10B981")}
                >
                  Entendido
                </button>
              </div>
            ) : (
              /* ── Formulário ── */
              <form onSubmit={recuperarSenha} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-medium mb-2 tracking-wide uppercase" style={{ color: "rgba(100,116,139,0.7)" }}>
                    Nome completo
                  </label>
                  <input
                    type="text"
                    placeholder="Digite seu nome completo…"
                    value={nomeRecuperar}
                    onChange={(e) => { setNomeRecuperar(e.target.value); setMsgRecuperacao(""); }}
                    autoFocus
                    className="w-full h-[52px] px-4 rounded-xl text-sm text-slate-100 placeholder:text-slate-700 focus:outline-none transition-colors"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}
                    onFocus={(e) => (e.target.style.borderColor = "rgba(77,141,255,0.55)")}
                    onBlur={(e)  => (e.target.style.borderColor = "rgba(255,255,255,0.09)")}
                  />
                </div>

                {/* Mensagens de status */}
                {msgRecuperacao === "default" && (
                  <div
                    className="rounded-xl px-4 py-3 text-[13px]"
                    style={{ background: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.2)", color: "#6ee7b7" }}
                  >
                    Sua senha padrão já é <strong>0000</strong>. Acesse normalmente com a senha 0000.
                  </div>
                )}
                {msgRecuperacao === "notfound" && (
                  <div
                    className="rounded-xl px-4 py-3 text-[13px]"
                    style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.2)", color: "#fcd34d" }}
                  >
                    Usuário não encontrado. Entre em contato com a administração para redefinir sua senha.
                  </div>
                )}
                {(msgRecuperacao === "error") && (
                  <div
                    className="rounded-xl px-4 py-3 text-[13px]"
                    style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.18)", color: "#fca5a5" }}
                  >
                    Erro ao processar. Entre em contato com a administração para redefinir sua senha.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={recuperandoSenha || !nomeRecuperar.trim()}
                  className="w-full h-[52px] flex items-center justify-center gap-2 rounded-xl font-semibold text-sm text-white transition-colors disabled:opacity-40"
                  style={{ background: "#2563EB" }}
                  onMouseEnter={(e) => { if (!recuperandoSenha) e.currentTarget.style.background = "#3B82F6"; }}
                  onMouseLeave={(e) => { if (!recuperandoSenha) e.currentTarget.style.background = "#2563EB"; }}
                >
                  {recuperandoSenha ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Aguarde…</>
                  ) : (
                    "Redefinir senha"
                  )}
                </button>

                <button
                  type="button"
                  onClick={fecharEsqueci}
                  className="w-full text-center text-[12px] py-1 transition-colors"
                  style={{ color: "rgba(100,116,139,0.5)", background: "none", border: "none", cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(100,116,139,0.5)")}
                >
                  Cancelar
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
